import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  DatePicker,
  Input,
  Segmented,
  Select,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  CATEGORY_OPTIONS,
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  rangeLabel,
  rangeParams,
  StatStrip,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type GroupBy = "medicine" | "day" | "bill";

interface Props {
  pharmacy: PharmacyHeader;
}

const ProfitReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [groupBy, setGroupBy] = useState<GroupBy>("medicine");
  const [category, setCategory] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(
    () => ({
      ...rangeParams(range),
      groupBy,
      category,
      search: search || undefined,
    }),
    [range, groupBy, category, search]
  );

  const load = useCallback(
    async (p = page, ps = pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.getProfitReport({
          ...buildParams(),
          page: p,
          limit: ps,
        });
        setData(res.data.data);
      } catch (e: any) {
        setError(e.response?.data?.message || "Failed to load profit report");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, page, pageSize]
  );

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, groupBy, category]);

  const summary = data?.summary || {};
  const returns = summary.returns || {};

  const labelTitle =
    groupBy === "day" ? "Date" : groupBy === "bill" ? "Bill #" : "Medicine";

  const labelValue = (r: any) => {
    if (groupBy === "day") {
      return r.label ? dayjs(r.label).format("DD/MM/YYYY") : "";
    }
    if (groupBy === "bill") return r.billNumber || "";
    return r.label || "";
  };

  const exportColumns: ExportColumn[] = [
    { title: labelTitle, value: labelValue },
    ...(groupBy === "medicine"
      ? [
          { title: "Category", value: (r: any) => r.category || "" },
          { title: "Manufacturer", value: (r: any) => r.manufacturer || "" },
        ]
      : []),
    ...(groupBy === "bill"
      ? [{ title: "Customer", value: (r: any) => r.customerName || "" }]
      : []),
    { title: "Qty", value: (r) => r.qty || 0 },
    { title: "Bills", value: (r) => r.billCount || 0 },
    { title: "Taxable Sales", value: (r) => (r.salesValue || 0).toFixed(2) },
    { title: "Cost (incl. GST)", value: (r) => (r.costValue || 0).toFixed(2) },
    { title: "Gross Profit", value: (r) => (r.grossProfit || 0).toFixed(2) },
    { title: "Margin %", value: (r) => (r.marginPct || 0).toFixed(2) },
  ];

  const fetchAll = async () => {
    const res = await adminApi.getProfitReport({
      ...buildParams(),
      format: "all",
    });
    return res.data.data;
  };

  const summaryLines = (s: any) => {
    const ret = s.returns || {};
    return [
      `Sales profit (period bills): taxable ${(s.salesValue || 0).toFixed(2)} - cost incl. GST ${(s.costValue || 0).toFixed(2)} = ${(s.grossProfit || 0).toFixed(2)} (${(s.marginPct || 0).toFixed(2)}%)`,
      `Returns in period: ${ret.returnCount || 0} returns, qty ${ret.qtyReturned || 0}, net refund ${(ret.netRefund || 0).toFixed(2)}, profit reversed ${(ret.profitReversed || 0).toFixed(2)}`,
      `Net profit (sales profit - profit reversed): ${(s.netProfit || 0).toFixed(2)}`,
    ];
  };

  const handleExportCsv = async () => {
    const full = await fetchAll();
    exportCsv("Profit Report", exportColumns, full.rows || [], [
      `Period: ${rangeLabel(range)} | Grouped by: ${groupBy}`,
      ...summaryLines(full.summary || {}),
    ]);
  };

  const handleExportPdf = async () => {
    const full = await fetchAll();
    const s = full.summary || {};
    const ret = s.returns || {};
    exportPdf({
      title: "Profit Report",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)} | Grouped by: ${groupBy}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: full.rows || [],
      summary: [
        { label: "Sales profit", value: pdfCurrency(s.grossProfit) },
        { label: "Margin", value: `${(s.marginPct || 0).toFixed(2)}%` },
        {
          label: "Returns in period",
          value: `${ret.returnCount || 0} (net refund ${pdfCurrency(ret.netRefund)})`,
        },
        { label: "Profit reversed", value: pdfCurrency(ret.profitReversed) },
        { label: "Net profit", value: pdfCurrency(s.netProfit) },
      ],
    });
  };

  const reset = () => {
    setRange(DEFAULT_RANGE);
    setGroupBy("medicine");
    setCategory(undefined);
    setSearch("");
  };

  return (
    <ReportShell
      title="Profit"
      loading={loading}
      error={error}
      onRefresh={() => load()}
      onReset={reset}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <RangePicker
            size="small"
            value={range}
            allowClear={false}
            presets={RANGE_PRESETS}
            onChange={(v) => v && setRange(v as DateRange)}
          />
          <Segmented
            size="small"
            value={groupBy}
            onChange={(v) => setGroupBy(v as GroupBy)}
            options={[
              { value: "medicine", label: "By medicine" },
              { value: "day", label: "By day" },
              { value: "bill", label: "By bill" },
            ]}
          />
          <Select
            size="small"
            allowClear
            placeholder="Category"
            style={{ width: 130 }}
            value={category}
            onChange={setCategory}
            options={CATEGORY_OPTIONS}
          />
          <Input.Search
            size="small"
            placeholder="Medicine name"
            style={{ width: 170 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1);
              load(1, pageSize);
            }}
            allowClear
          />
        </>
      }
      stats={[
        {
          title: "Taxable sales (period bills)",
          value: summary.salesValue || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Cost incl. GST",
          value: summary.costValue || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Sales profit",
          value: summary.grossProfit || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Margin",
          value: summary.marginPct || 0,
          suffix: "%",
          precision: 2,
        },
        { title: "Qty sold", value: summary.qty || 0 },
      ]}
      extra={
        <>
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            styles={{ body: { padding: "8px 12px" } }}
            title={
              <span style={{ fontSize: 13 }}>
                Returns during this period (any bill date)
              </span>
            }
          >
            <StatStrip
              loading={loading}
              stats={[
                { title: "Returns", value: returns.returnCount || 0 },
                { title: "Qty returned", value: returns.qtyReturned || 0 },
                {
                  title: "Net refund paid",
                  value: returns.netRefund || 0,
                  prefix: "₹",
                  precision: 2,
                },
                {
                  title: "Sales reversed",
                  value: returns.salesReversed || 0,
                  prefix: "₹",
                  precision: 2,
                },
                {
                  title: "Profit reversed",
                  value: returns.profitReversed || 0,
                  prefix: "₹",
                  precision: 2,
                },
                {
                  title: "Net profit (period)",
                  value: summary.netProfit || 0,
                  prefix: "₹",
                  precision: 2,
                },
              ]}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sales profit counts bills dated in the period. Returns dated in
              the period are shown separately (even when the original bill is
              from an earlier month). Net profit = sales profit − profit
              reversed on returned items.
            </Text>
          </Card>
        </>
      }
    >
      <Table
        rowKey={(r: any) => String(r._id)}
        size="small"
        sticky
        loading={loading}
        dataSource={data?.rows || []}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total: data?.pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} rows`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
        columns={[
          {
            title: labelTitle,
            fixed: "left",
            ellipsis: true,
            render: (_: unknown, r: any) => labelValue(r),
          },
          ...(groupBy === "medicine"
            ? [
                { title: "Category", dataIndex: "category", width: 90 },
                {
                  title: "Manufacturer",
                  dataIndex: "manufacturer",
                  width: 130,
                  ellipsis: true,
                },
              ]
            : []),
          ...(groupBy === "bill"
            ? [
                {
                  title: "Customer",
                  dataIndex: "customerName",
                  width: 140,
                  ellipsis: true,
                  render: (v: string) => v || "Walk-in",
                },
              ]
            : []),
          { title: "Qty", dataIndex: "qty", width: 70, align: "right" as const },
          {
            title: "Bills",
            dataIndex: "billCount",
            width: 70,
            align: "right" as const,
          },
          {
            title: "Taxable sales",
            dataIndex: "salesValue",
            width: 120,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Cost incl. GST",
            dataIndex: "costValue",
            width: 120,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Profit",
            dataIndex: "grossProfit",
            width: 110,
            align: "right" as const,
            render: (v: number) =>
              v < 0 ? (
                <Text type="danger">{formatCurrency(v)}</Text>
              ) : (
                <Text type="success">{formatCurrency(v)}</Text>
              ),
          },
          {
            title: "Margin",
            dataIndex: "marginPct",
            width: 90,
            align: "right" as const,
            render: (v: number) => `${(v || 0).toFixed(1)}%`,
          },
        ]}
      />
    </ReportShell>
  );
};

export default ProfitReport;
